import React, { useState, useEffect, useCallback } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { fetchProductDetail, selectProductDetailState, resetProductDetailState, addToCart, selectIsUserLoggedIn, selectUser } from '../../../redux/WithcookieSlice';
import { PriceDisplay } from '../../common/PriceDisplay';
import SafeImage from '../../common/SafeImage';
import { useTranslation } from '../../../redux/TranslationHelper';
import { Product, Review, Variant, OptionGroup, OptionValue } from './ProductDetail.types';
import { adaptProductDetailProps, errorToString } from './ProductDetail.adapter';
import './ProductDetail.css';

/**
 * 상품 상세 컴포넌트
 * @param props 
 * @returns 
 */
const ProductDetailComponent: React.FC<any> = (props) => {
  // 어댑터를 통해 props 변환
  const adaptedProps = adaptProductDetailProps(props);
  const {
    id, 
    style = {}, 
    componentProps = {}, 
    pathVariables = {},
    content = {},
    imageUrl,
    title,
    description,
    debug = false,
    viewMode,
    isPreview
  } = adaptedProps;
  
  // 다국어 지원 훅 사용
  const { t } = useTranslation();
  
  // URL 파라미터에서 ID 가져오기
  const { id: urlProductId } = useParams<{ id: string }>();
  
  // URL 경로 변수에서 productId를 우선적으로 가져오고, 없으면 componentProps의 값 사용
  const { 
    order = "", 
    none = "none", 
    lg = 9, 
    productId: propsProductId = 0,
    showDescriptionTab = true,
    showReviewsTab = true,
    showSpecificationTab = true,
    showStock = false
  } = componentProps;
  
  // dispatch에 타입 단언을 사용하여 타입 오류 해결
  const dispatch = useDispatch() as any;
  const { productData, loading, error } = useSelector(selectProductDetailState);
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const userInfo = useSelector(selectUser);
  
  // URL 파라미터, 경로 변수, props 순으로 productId 결정
  const productId = urlProductId 
    ? parseInt(urlProductId, 10) 
    : (pathVariables?.id 
      ? parseInt(pathVariables.id, 10) 
      : propsProductId);

  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [selectedOptions, setSelectedOptions] = useState<Record<number, number>>({});
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null);
  
  // 보이는 탭 중 첫 번째 탭을 기본값으로 설정
  const getInitialActiveTab = useCallback(() => {
    if (showDescriptionTab) return 'description';
    if (showReviewsTab) return 'reviews';
    if (showSpecificationTab) return 'specification';
    return 'description'; // 모든 탭이 숨겨진 경우 (실제로는 표시되지 않음)
  }, [showDescriptionTab, showReviewsTab, showSpecificationTab]);
  
  const [activeTab, setActiveTab] = useState(getInitialActiveTab());
  
  // 탭 표시 여부가 변경될 때 활성 탭 업데이트
  useEffect(() => {
    // 현재 활성 탭이 표시되지 않는 경우 다른 탭으로 변경
    if (
      (activeTab === 'description' && !showDescriptionTab) ||
      (activeTab === 'reviews' && !showReviewsTab) ||
      (activeTab === 'specification' && !showSpecificationTab)
    ) {
      setActiveTab(getInitialActiveTab());
    }
  }, [showDescriptionTab, showReviewsTab, showSpecificationTab, activeTab, getInitialActiveTab]);
  
  // 컴포넌트 언마운트 시 상품 상세 상태 초기화
  useEffect(() => {
    return () => {
      dispatch(resetProductDetailState());
    };
  }, [dispatch]);
  
  // API에서 상품 데이터 가져오기
  useEffect(() => {
    if (productId) {
      dispatch(fetchProductDetail(productId));
    }
  }, [dispatch, productId]); // productId가 변경될 때마다 데이터 다시 가져오기
  
  const increaseQuantity = () => {
    setQuantity(prevQuantity => prevQuantity + 1);
  };

  const decreaseQuantity = () => {
    setQuantity(prevQuantity => prevQuantity > 1 ? prevQuantity - 1 : 1);
  };

  // 별점 컴포넌트
  const StarRating: React.FC<{ rating?: number }> = ({ rating = 4 }) => {
    // rating이 숫자인지 확인하고 유효한 범위(0-5)로 제한
    const safeRating = typeof rating === 'number' && !isNaN(rating) 
      ? Math.min(Math.max(0, rating), 5) 
      : 4;
    
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (i <= safeRating) {
        stars.push(<i key={i} className="gicon gi-star fill"></i>);
      } else {
        stars.push(<i key={i} className="gicon gi-star-o"></i>);
      }
    }
    return <>{stars}</>;
  };

  // 수량 선택 컴포넌트
  const QuantitySelector: React.FC = () => {
    return (
      <div className="quantity-selector">
        <span className="quantity-label"></span>
        <button className="qty-down" onClick={decreaseQuantity}>-</button>
        <input className="qty-input" type="number" value={quantity} readOnly />
        <button className="qty-up" onClick={increaseQuantity}>+</button>
      </div>
    );
  };
  
  // 마크다운 형식의 이미지 URL 추출
  const extractImagesFromMarkdown = (markdown?: string): string[] => {
    if (!markdown) return [];
    const regex = /!\[.*?\]\((.*?)\)/g;
    const matches: string[] = [];
    let match;
    
    while ((match = regex.exec(markdown)) !== null) {
      matches.push(match[1]);
    }
    
    return matches;
  };
  
  // 타입 안전성 보장을 위한 상품 데이터 처리
  const product = productData?.product || productData as unknown as Product;
  
  // 메인 이미지와 추가 이미지들을 하나의 배열로 결합
  const allImages = [
    product?.config?.img_url || product?.config?.main_image || product?.mainImage,
    ...(product?.additionalImages || [])
  ].filter(Boolean);
  
  // 선택된 이미지가 없으면 첫 번째 이미지를 선택
  useEffect(() => {
    if (allImages.length > 0 && !selectedImage) {
      setSelectedImage(allImages[0]);
    }
  }, [product?.config?.img_url, product?.config?.main_image, product?.mainImage, product?.additionalImages]);
  
  // 선택된 옵션에 따라 variant 찾기
  useEffect(() => {
    if (!product?.variants || !product?.optionGroups) return;
    
    // 모든 필수 옵션이 선택되었는지 확인
    const requiredGroups = product.optionGroups.filter((g: OptionGroup) => g.isRequired);
    const allRequiredSelected = requiredGroups.every((g: OptionGroup) => selectedOptions[g.id]);
    
    if (!allRequiredSelected) {
      setSelectedVariant(null);
      return;
    }
    
    // 선택된 옵션들과 일치하는 variant 찾기
    const matchingVariant = product.variants.find((variant: Variant) => {
      if (!variant.options || variant.options.length === 0) return false;
      
      return variant.options.every((variantOption: any) => {
        const groupId = variantOption.optionValue.optionGroupId;
        return selectedOptions[groupId] === variantOption.optionValueId;
      });
    });
    
    setSelectedVariant(matchingVariant || null);
  }, [selectedOptions, product?.variants, product?.optionGroups]);
  
  // 옵션 선택 핸들러
  const handleOptionSelect = (groupId: number, valueId: number) => {
    setSelectedOptions(prev => ({
      ...prev,
      [groupId]: valueId
    }));
  };
  
  // 선택한 옵션 ID를 코드로 변환하는 함수
  const getOptionsForCart = () => {
    const options: Record<string, string> = {};
    
    if (!product?.optionGroups || !selectedVariant) return options;
    
    product.optionGroups.forEach((group: OptionGroup) => {
      const selectedValueId = selectedOptions[group.id];
      if (selectedValueId) {
        const selectedValue = group.optionValues?.find((v: OptionValue) => v.id === selectedValueId);
        if (selectedValue) {
          options[group.code] = selectedValue.code;
        }
      }
    });
    
    return options;
  };
  
  // 등급별 가격 계산 함수 - 기본 가격에만 적용
  const calculateLevelPrice = (basePrice: number) => {
    // 사용자 정보가 없거나 로그인하지 않은 경우
    if (!userInfo || !isUserLoggedIn) {
      return {
        originalPrice: basePrice,
        levelPrice: basePrice,
        discount: 0,
        discountRate: 0,
        levelName: null
      };
    }
    
    // 사용자의 level2 ID 확인
    const userLevel2Id = userInfo.level2;
    const userLevel2Policy = userInfo.level2Policy;
    
    // level2가 없으면 할인 없음
    if (!userLevel2Id || !userLevel2Policy) {
      return {
        originalPrice: basePrice,
        levelPrice: basePrice,
        discount: 0,
        discountRate: 0,
        levelName: null
      };
    }
    
    // 상품의 optionJson에서 level2_price 확인
    let levelPrice = basePrice;
    if (product?.optionJson?.level2_price && product.optionJson.level2_price[userLevel2Id]) {
      // level2_price에 해당 레벨의 가격이 있으면 그 가격 사용
      levelPrice = product.optionJson.level2_price[userLevel2Id];
    } else if (userLevel2Policy?.settingJson?.supply_price_ratio) {
      // level2_price가 없으면 supply_price_ratio 사용
      const supplyPriceRatio = userLevel2Policy.settingJson.supply_price_ratio;
      levelPrice = Math.floor(basePrice * supplyPriceRatio);
    }
    
    const discount = basePrice - levelPrice;
    const discountRate = basePrice > 0 ? Math.round((discount / basePrice) * 100) : 0;
    
    return {
      originalPrice: basePrice,
      levelPrice,
      discount,
      discountRate,
      levelName: userLevel2Policy.customName || userLevel2Policy.type
    };
  };

  
  
  // 로딩 중 표시
  if (loading) {
    return (
      <div className="product-loading">
        <p>{t('상품 정보를 불러오는 중입니다...')}</p>
      </div>
    );
  }
  
  // 오류 발생 시 표시
  if (error && !productData) {
    const errorMessage = errorToString(error);
    return (
      <div className="product-error">
        <p>{errorMessage}</p>
      </div>
    );
  }
  
  // 데이터가 없는 경우
  if (!productData) {
    return (
      <div className="no-product-data">
        <p>{t('상품 정보를 찾을 수 없습니다.')}</p>
      </div>
    );
  }
  
  // 설명에서 이미지 추출
  const descriptionImages = extractImagesFromMarkdown(productData.product?.description || productData.description);

  // 장바구니 추가 함수
  const handleAddToCart = () => {
    if (productData && productId) {
      try {
        // 1. Variant가 있는 경우
        if (product?.variants && product.variants.length > 0) {
          // 옵션 그룹이 있고 필수 옵션이 있는 경우
          if (product?.optionGroups && product.optionGroups.length > 0) {
            const requiredGroups = product.optionGroups.filter((g: OptionGroup) => g.isRequired);
            const allRequiredSelected = requiredGroups.every((g: OptionGroup) => selectedOptions[g.id]);
            
            if (!allRequiredSelected) {
              alert(t('필수 옵션을 선택해주세요.'));
              return;
            }
            
            if (!selectedVariant) {
              alert(t('상품 옵션을 선택해주세요.'));
              return;
            }
            
            // variant 재고 확인
            if (selectedVariant.stock === 0) {
              alert(t('품절된 상품입니다.'));
              return;
            }
          }
          
          // Variant가 있는 상품 - variant_id와 options 포함
          const cartData = {
            product_id: productId,
            variant_id: selectedVariant?.id,
            count: quantity,
            options: getOptionsForCart()
          };
          
          dispatch(addToCart(cartData))
            .unwrap()
            .then(() => {
              // 성공 메시지
              const message = t('상품이 장바구니에 추가되었습니다.') + ` (${quantity}` + t('개') + `)`;
              alert(message);
              
              // 옵션: 장바구니 페이지로 이동하거나 현재 페이지에 남을 수 있도록 확인
              if (window.confirm(t('장바구니 페이지로 이동하시겠습니까?'))) {
                window.location.href = '/cart';
              }
            })
            .catch((error: any) => {
              console.error('장바구니 추가 오류:', error);
              
              // 로그인이 필요한 경우
              if (error.code === 'AUTH_REQUIRED') {
                if (window.confirm(t('로그인이 필요한 서비스입니다. 로그인 페이지로 이동하시겠습니까?'))) {
                  window.location.href = '/login';
                }
              } else {
                // 기타 오류
                alert(t('장바구니 추가 실패') + ': ' + errorToString(error));
              }
            });
        } 
        // 2. Variant가 없는 경우 - 단순 상품으로 처리
        else {
          // 기본 재고 확인
          if (product?.config?.stock_count === 0) {
            alert(t('품절된 상품입니다.'));
            return;
          }
          
          // Variant 없이 장바구니 추가
          const cartData = {
            product_id: productId,
            count: quantity
          };
          
          dispatch(addToCart(cartData))
            .unwrap()
            .then(() => {
              // 성공 메시지
              const message = t('상품이 장바구니에 추가되었습니다.') + ` (${quantity}` + t('개') + `)`;
              alert(message);
              
              // 옵션: 장바구니 페이지로 이동하거나 현재 페이지에 남을 수 있도록 확인
              if (window.confirm(t('장바구니 페이지로 이동하시겠습니까?'))) {
                window.location.href = '/cart';
              }
            })
            .catch((error: any) => {
              console.error('장바구니 추가 오류:', error);
              
              // 로그인이 필요한 경우
              if (error.code === 'AUTH_REQUIRED') {
                if (window.confirm(t('로그인이 필요한 서비스입니다. 로그인 페이지로 이동하시겠습니까?'))) {
                  window.location.href = '/login';
                }
              } else {
                // 기타 오류
                alert(t('장바구니 추가 실패') + ': ' + errorToString(error));
              }
            });
        }
      } catch (error) {
        console.error('예상치 못한 오류:', error);
        alert(t('예상치 못한 오류') + ': ' + errorToString(error));
      }
    } else {
      alert(t('상품 정보가 유효하지 않습니다.'));
    }
  };

  return (
    <section className="product-detail-container">
      <div className="container-fluid">
        <Row className="justify-content-center">
          <Col
            lg={7}
            md={12}
            className={`gi-pro-rightside gi-common-rightside ${order}`}
            style={{maxWidth: "100%", padding: "0 15px"}}
          >
            {/* Single product content Start */}
            <div className="single-pro-block">
              <div className="single-pro-inner">
                <Row>
                  <Col sm={12} md={6} className="single-pro-img">
                    <div className="single-product-scroll">
                      {/* 메인 이미지 직접 표시 */}
                      <div className="main-product-image">
                        {/* @ts-ignore SafeImage 컴포넌트 prop 타입 오류 무시 */}
                        <SafeImage 
                          src={selectedImage || product?.config?.img_url || 
                              product?.mainImage} 
                          alt={product?.title || product?.name || t('상품명')} 
                          style={{width: "100%", height: "auto", borderRadius: "5px", marginBottom: "15px"}} 
                          placeholderStyle={{
                            width: "100%",
                            paddingTop: "75%", // 4:3 비율
                            backgroundColor: "#cccccc",
                            borderRadius: "5px",
                            marginBottom: "15px",
                            position: "relative"
                          }}
                        />
                      </div>
                      
                      {/* 추가 이미지 썸네일 */}
                      {allImages.length > 1 && (
                        <div className="product-image-thumbnails">
                          {allImages.map((imgUrl, index) => (
                            <div 
                              key={index} 
                              className={`thumbnail-item ${selectedImage === imgUrl ? 'active' : ''}`}
                              onClick={() => setSelectedImage(imgUrl)}
                              onMouseEnter={() => setSelectedImage(imgUrl)}
                            >
                              {/* @ts-ignore SafeImage 컴포넌트 prop 타입 오류 무시 */}
                              <SafeImage 
                                src={imgUrl} 
                                alt={`${product?.title || t('상품명')} ${index + 1}`} 
                                style={{width: "100%", height: "100%", objectFit: "cover"}} 
                                placeholderStyle={{
                                  width: "100%",
                                  height: "100%",
                                  backgroundColor: "#cccccc"
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={12} md={6} className="single-pro-desc">
                    <div className="single-pro-content">
                      <h5 className="gi-single-title">
                        {t(product?.title as string) || t(product?.name as string) || t('상품명')}
                      </h5>
                      <div className="gi-single-rating-wrap">
                        <div className="gi-single-rating">
                          <StarRating rating={product?.averageRating} />
                        </div>
                   
                      </div>

                      <div className="gi-single-price-stoke">
                        <div className="gi-single-price">
                          {(() => {
                            // 기본 가격 계산
                            const basePrice = product?.config?.discounted_price || 
                                             product?.config?.default_price || 
                                             product?.price || 0;
                            
                            // 등급별 가격 계산 (기본 가격에만 적용)
                            const priceInfo = calculateLevelPrice(basePrice);
                            
                            // 등급 할인이 있는 경우
                            if (priceInfo.discount > 0 && priceInfo.levelName) {
                              return (
                                <>
                                  {/* 등급 특별가 라벨 */}
                                  <div className="level-price-label">
                                    <span className="level-badge">{priceInfo.levelName} 특별가</span>
                                    <span className="discount-rate">-{priceInfo.discountRate}%</span>
                                  </div>
                                  
                                  {/* 가격 표시 */}
                                  <div className="price-display">
                                    <span className="original-price">
                                      {/* @ts-ignore */}
                                      <PriceDisplay price={priceInfo.originalPrice} />
                                    </span>
                                    <span className="level-price">
                                      {/* @ts-ignore */}
                                      <PriceDisplay price={priceInfo.levelPrice} />
                                    </span>
                                  </div>
                                  
                                  {/* 절약 금액 */}
                                  <div className="saved-amount">
                                    {priceInfo.discount.toLocaleString()}원 절약
                                  </div>
                                </>
                              );
                            } else {
                              // 등급 할인이 없는 경우
                              return (
                                <div className="final-price">
                                  {/* @ts-ignore */}
                                  <PriceDisplay price={priceInfo.originalPrice} />
                                </div>
                              );
                            }
                          })()}
                        </div>
                        {showStock && (
                          <div className="gi-single-stoke">
                            <span className="gi-single-sku">{t("상품코드")}: {product?.sku || product?.id || ""}</span>
                            {product?.config?.stock_count === 0 && (
                              <span className="gi-single-ps-title out-of-stock">
                                {t("품절")}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* 태그 정보 있으면 표시 */}
                      {(product?.tags) && (product.tags).trim() !== "" && (
                        <div className="gi-single-tags">
                          <strong>{t("태그")}:</strong> {product.tags}
                        </div>
                      )}

                      {product?.features && product.features.length > 0 && (
                        <div className="gi-single-list">
                          <ul>
                            {product.features.map((feature: {label: string, value: string}, index: number) => (
                              <li key={index}>
                                <strong>{feature.label} :</strong> {feature.value}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {product?.weights && product.weights.length > 0 && (
                        <div className="gi-pro-variation">
                          <div className="gi-pro-variation-inner gi-pro-variation-size">
                            <span>{t("무게")}</span>
                            <div className="gi-pro-variation-content">
                              <ul>
                                {product.weights.map((weight: string, index: number) => (
                                  <li key={index} className={index === 0 ? "active" : ""}>
                                    <span>{weight}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* 옵션 선택 UI - Variant가 있는 경우에만 표시 */}
                      {product?.variants && product.variants.length > 0 && 
                       product?.optionGroups && product.optionGroups.length > 0 && (
                        <div className="gi-product-options">
                          {product.optionGroups.map((group: OptionGroup) => (
                            <div key={group.id} className="gi-option-group">
                              <div className="gi-option-label">
                                <span>{group.name}</span>
                                {group.isRequired && <span className="required">*</span>}
                              </div>
                              <div className="gi-option-values">
                                {group.optionValues?.map((value: OptionValue) => (
                                  <button
                                    key={value.id}
                                    className={`gi-option-button ${selectedOptions[group.id] === value.id ? 'active' : ''}`}
                                    onClick={() => handleOptionSelect(group.id, value.id)}
                                    disabled={!value.isActive}
                                  >
                                    {value.value}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* 선택된 variant 정보 표시 */}
                      {product?.variants && product.variants.length > 0 && selectedVariant && (
                        <div className="gi-variant-info">
                          <div className="gi-variant-sku">
                            {t("선택된 상품")}: {selectedVariant.sku}
                          </div>
                          {Number(selectedVariant.additionalPrice) > 0 && (
                            <div className="gi-variant-price">
                              <span>{t("추가 금액")}: </span>
                              <span className="additional-price-amount">
                                +₩{Number(selectedVariant.additionalPrice).toLocaleString()}
                              </span>
                            </div>
                          )}
                          {selectedVariant.stock === 0 && (
                            <div className="gi-variant-stock out-of-stock">
                              {t("품절")}
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="gi-single-qty">
                        <div className="qty-plus-minus">
                          <QuantitySelector />
                        </div>
                        <div className="gi-single-cart">
                          <button 
                            className={`btn btn-primary gi-btn-1 ${
                              // Variant가 있고 선택된 variant가 품절인 경우
                              (product?.variants?.length > 0 && selectedVariant && selectedVariant.stock === 0) ||
                              // Variant가 없고 기본 재고가 0인 경우
                              (!product?.variants?.length && product?.config?.stock_count === 0) 
                              ? 'out-of-stock' : ''
                            }`} 
                            onClick={handleAddToCart}
                            disabled={
                              // Variant가 있고 선택된 variant가 품절인 경우
                              (product?.variants?.length > 0 && selectedVariant && selectedVariant.stock === 0) ||
                              // Variant가 없고 기본 재고가 0인 경우
                              (!product?.variants?.length && product?.config?.stock_count === 0)
                            }
                          >
                            {(
                              (product?.variants?.length > 0 && selectedVariant && selectedVariant.stock === 0) ||
                              (!product?.variants?.length && product?.config?.stock_count === 0)
                            ) ? t('품절') : t('장바구니 추가')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
            {/* Single product content End */}
            
            {/* Product Tab Start */}
            {(showDescriptionTab || showReviewsTab || showSpecificationTab) && (
              <div className="gi-pro-tab gi-pro-tab-style1">
                <ul className="nav nav-tabs gi-pro-tab-nav" role="tablist">
                  {showDescriptionTab && (
                    <li className="nav-item">
                      <a 
                        className={`nav-link ${activeTab === 'description' ? 'active' : ''}`} 
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveTab('description');
                        }}
                        href="#description"
                        role="tab"
                      >
                        {t('상품 설명')}
                      </a>
                    </li>
                  )}
                  {showReviewsTab && (
                    <li className="nav-item">
                      <a 
                        className={`nav-link ${activeTab === 'reviews' ? 'active' : ''}`} 
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveTab('reviews');
                        }}
                        href="#reviews" 
                        role="tab"
                      >
                        {t('리뷰')}
                      </a>
                    </li>
                  )}
                </ul>
                <div className="tab-content gi-pro-tab-content">
                  {showDescriptionTab && (
                    <div className={`tab-pane fade ${activeTab === 'description' ? 'show active' : ''}`} id="description" role="tabpanel">
                      <div className="gi-description-block">
                        <div className="gi-description-detail">
                          {descriptionImages.length > 0 ? (
                            <div className="description-images">
                              {descriptionImages.map((imgUrl: string, index: number) => (
                                <div key={index} style={{marginBottom: "15px"}}>
                                  {/* @ts-ignore SafeImage 컴포넌트 prop 타입 오류 무시 */}
                                  <SafeImage 
                                    src={imgUrl} 
                                    alt={t('상품 상세 이미지') + ' ' + (index + 1)} 
                                    style={{width: "100%", height: "auto"}} 
                                    placeholderStyle={{
                                      width: "100%",
                                      paddingTop: "75%", // 4:3 비율
                                      backgroundColor: "#cccccc",
                                      marginBottom: "15px",
                                      position: "relative"
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          ) : product?.description ? (
                            <div dangerouslySetInnerHTML={{ 
                              __html: product.description.replace(
                                /!\[(.*?)\]\((.*?)\)/g, 
                                '<img src="$2" alt="$1" style="width:100%; height:auto; margin:10px 0;" />'
                              ) 
                            }} />
                          ) : (
                            <p>{t('상품 설명이 없습니다.')}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {showReviewsTab && (
                    <div className={`tab-pane fade ${activeTab === 'reviews' ? 'show active' : ''}`} id="reviews" role="tabpanel">
                      <div className="gi-desc-reviews">
                        <div className="gi-reviews-wrapper">
                          <h4 className="gi-reviews-title">{t('고객 리뷰')}</h4>
                          {product?.recentReviews && product.recentReviews.length > 0 ? (
                            <div className="gi-review-box">
                              {product.recentReviews.map((review: Review, index: number) => (
                                <div className="gi-review" key={index}>
                                  
                                  <div className="gi-review-content">
                                    <div className="gi-review-top">
                                      <div className="gi-review-name">{review.userName || t("사용자")}</div>
                                      <div className="gi-rating-star">
                                        <StarRating rating={review.rating || 0} />
                                      </div>
                                      <div className="gi-review-date">
                                        {new Date(review.createdAt).toLocaleDateString()}
                                      </div>
                                    </div>
                                    <div className="gi-review-bottom">
                                      <div dangerouslySetInnerHTML={{ __html: review.content }} />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="gi-review-box">
                              <p>{t('아직 리뷰가 없습니다.')}</p>
                            </div>
                          )}
                          {product?.totalReviews && product.totalReviews > 0 && (
                            <div className="gi-review-stats">
                              <p>{t('총 리뷰 수')}: {product.totalReviews}{t('개')}</p>
                              <p>{t('평균 평점')}: <StarRating rating={product.averageRating || 0} /> ({product.averageRating ? product.averageRating.toFixed(1) : '0.0'})</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Product Tab End */}
          </Col>
          
          {/* Optional Sidebar */}
          {none !== "none" && (
            <Col lg={3} md={12} className={`gi-common-leftside ${order}`}>
              <div className="gi-sidebar-wrapper">
                <div className="gi-sidebar-block">
                  <div className="gi-sidebar-title">
                    <h3>{t("카테고리")}</h3>
                  </div>
                  <div className="gi-sidebar-content">
                    <ul className="gi-category-list">
                      <li><a href="/category/snacks">{t("간식")}</a></li>
                      <li><a href="/category/beverages">{t("음료")}</a></li>
                      <li><a href="/category/dairy">{t("유제품")}</a></li>
                      <li><a href="/category/fruits-vegetables">{t("과일 & 채소")}</a></li>
                    </ul>
                  </div>
                </div>
                {productData.relatedProducts && Array.isArray(productData.relatedProducts) && productData.relatedProducts.length > 0 && (
                  <div className="gi-sidebar-block">
                    <div className="gi-sidebar-title">
                      <h3>{t('인기 상품')}</h3>
                    </div>
                    <div className="gi-sidebar-content">
                      <ul className="gi-popular-product">
                        {productData.relatedProducts.slice(0, 3).map((relatedProduct: any, index: number) => (
                          <li key={index}>
                            <div className="gi-pro-image">
                              {/* @ts-ignore SafeImage 컴포넌트 prop 타입 오류 무시 */}
                              <SafeImage 
                                src={relatedProduct.image} 
                                alt={relatedProduct.title || t("관련 상품")} 
                                placeholderStyle={{ width: "100%", height: "100px", backgroundColor: "#cccccc" }}
                              />
                            </div>
                            <div className="gi-pro-content">
                              <h5>{t(relatedProduct.title || "상품명")}</h5>
                              <div className="gi-rating-star">
                                <StarRating rating={typeof relatedProduct.rating === 'number' ? relatedProduct.rating : 5} />
                              </div>
                              <span className="gi-price">
                                <span className="new-price">${typeof relatedProduct.newPrice === 'number' ? relatedProduct.newPrice.toFixed(2) : "0.00"}</span>
                                <span className="old-price">${typeof relatedProduct.oldPrice === 'number' ? relatedProduct.oldPrice.toFixed(2) : "0.00"}</span>
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </Col>
          )}
        </Row>
      </div>
    </section>
  );
};

export default ProductDetailComponent; 